"use client";

import Link from "next/link";
import { FormEvent, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import type { CountryCode } from "libphonenumber-js";
import { MessageCircle } from "lucide-react";
import { AuthShell } from "@/components/auth-shell";
import { CountryDialPicker, PhoneDialRow } from "@/components/country-dial-picker";
import { usePrefs } from "@/components/prefs-provider";
import { Button } from "@/components/ui/button";
import { CodeInput, Field, Input } from "@/components/ui/field";
import { InlineError } from "@/components/ui/feedback";

type Step = "phone" | "code";

function mapError(code: string, t: ReturnType<typeof usePrefs>["t"]) {
  const m: Record<string, string> = {
    send_failed: t.auth.codeSendFailed,
    duplicate_send: t.auth.duplicateSend,
    invalid_code: t.auth.invalidCode,
    expired: t.auth.codeExpired,
    too_many_attempts: t.auth.tooManyAttempts,
    parent_phone_mismatch: t.auth.parentPhoneMismatch,
    invalid_phone: t.auth.invalidPhone,
    reporter_offline: t.auth.reporterOffline,
    phone_ambiguous: t.auth.phoneAmbiguous,
    not_found: t.auth.verificationExpired,
    verify_failed: t.auth.resetFailed,
    invalid_input: t.auth.invalidCode,
  };
  return m[code] || t.auth.resetFailed;
}

function normalizeCode(raw: FormDataEntryValue | null) {
  return String(raw ?? "").replace(/\s+/g, "").trim();
}

export default function ForgotPasswordPage() {
  const router = useRouter();
  const { t, locale } = usePrefs();
  const a = t.auth;
  const [step, setStep] = useState<Step>("phone");
  const [country, setCountry] = useState<CountryCode>("IL");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const verifyBusy = useRef(false);

  async function onPhone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const fd = new FormData(e.currentTarget);
    try {
      const res = await fetch("/api/auth/forgot-password/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: fd.get("phone"), country, locale }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(mapError(data.error || "", t));
        return;
      }
      setPhone(String(data.phone || ""));
      setStep("code");
    } catch {
      setError(a.resetFailed);
    } finally {
      setLoading(false);
    }
  }

  async function onCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (verifyBusy.current) return;
    verifyBusy.current = true;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/forgot-password/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: normalizeCode(new FormData(e.currentTarget).get("code")) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(mapError(data.error || "", t));
        verifyBusy.current = false;
        setLoading(false);
        return;
      }
      router.replace("/reset-password");
    } catch {
      setError(a.resetFailed);
      verifyBusy.current = false;
      setLoading(false);
    }
  }

  const footer = (
    <p>
      <Link href="/login" className="font-semibold text-brand underline-offset-2 hover:underline">
        {a.backToLogin}
      </Link>
    </p>
  );

  return (
    <AuthShell stepKey={step} title={a.forgotTitle} lead={step === "phone" ? a.forgotLeadPhone : a.parentCodeLead} footer={footer}>
      {step === "phone" ? (
        <form onSubmit={onPhone} className="space-y-4">
          <Field label={a.parentPhone} hint={a.parentPhoneHint} required>
            {(ctl) => (
              <PhoneDialRow>
                <CountryDialPicker value={country} onChange={setCountry} label={t.dash.parentCountry} />
                <Input
                  {...ctl}
                  name="phone"
                  type="tel"
                  ltr
                  required
                  autoFocus
                  autoComplete="tel-national"
                  inputMode="numeric"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="050-000-0000"
                  className="min-w-0 flex-1"
                />
              </PhoneDialRow>
            )}
          </Field>
          <InlineError message={error} />
          <Button type="submit" size="lg" className="w-full" loading={loading} icon={<MessageCircle className="h-4 w-4" aria-hidden />}>
            {loading ? a.sendingCode : a.sendParentCode}
          </Button>
        </form>
      ) : (
        <form onSubmit={onCode} className="space-y-5">
          <Field label={a.verificationCode} hint={a.codeHintWhatsapp} required>
            {(ctl) => <CodeInput {...ctl} name="code" required autoFocus placeholder="••••••" />}
          </Field>
          <InlineError message={error} />
          <Button type="submit" size="lg" className="w-full" loading={loading}>
            {loading ? a.verifying : a.continue}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep("phone");
              setError("");
            }}
          >
            {t.common.back}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
